import { Reducer, Action } from "redux";
import { AppThunkAction } from ".";
import { Coverage, CoveragePrice } from "./Insurance";
import { IAddOn, SetAddonPrices } from "./AddOn";
import { DeepClone, GetIntFromString } from "../helperFunctions";

export interface Price {
  title: string;
  price: string;
}

export interface FooterState {
  title: string;
  price: string;
  totalPriceTooltip: string;
  purchaseButton: string;
  prices?: Price[];
  expanded: boolean;
  secondaryPrice: CoveragePrice;
}

export const footerDefaults = {
  title: "Totalt pris:",
  price: "341 kr/mån",
  purchaseButton: "Purchase",
  totalPriceTooltip: "Din försäkring börjar gälla den dag din bil levereras.",
  expanded: false,
  secondaryPrice: {} as CoveragePrice,
};

export function PreparePriceList(currentCoverage: Coverage, selectedAddons: string[]): Price[] {
  const result = [] as Price[];
  const primaryPrice = currentCoverage.PrimaryPrice;
  const period = primaryPrice.Period;
  let coverageAddons: IAddOn[] = currentCoverage.CoverageAddons?.filter(x => selectedAddons.includes(x.InternalCode ?? "")) ?? [];

  coverageAddons = DeepClone(coverageAddons);
  const addonSum = SetAddonPrices(primaryPrice, coverageAddons);

  result.push({
    title: currentCoverage.LongName,
    price: `${GetIntFromString(primaryPrice.Amount) - addonSum} ${primaryPrice.Currency}/${period}`,
  });

  coverageAddons.forEach(x => {
    //replace "fran" here because it should be hidden in the footer area
    //will be removed after dynamic prices are fixed in IMF

    result.push({
      title: x?.texts?.header ?? x.Name,
      price: x?.texts?.price ?? x.MonthlyPrice,
    });
  });

  result.push({
    title: "Totalt pris:",
    price: `${primaryPrice.Amount} ${primaryPrice.Currency}/${period}`,
  });

  return result;
}

export function PreparePriceListV2(currentCoverage: Coverage, selectedAddons: string[]): Price[] {
  const result = [] as Price[];
  const primaryPrice = currentCoverage?.PrimaryPrice;
  const period = primaryPrice?.Period;
  const currency = primaryPrice?.Currency;
  let coverageAddons: IAddOn[] = currentCoverage.CoverageAddons?.filter(x => selectedAddons.includes(x.InternalCode ?? "")) ?? [];

  coverageAddons = DeepClone(coverageAddons);

  result.push({
    title: currentCoverage.LongName,
    price: `${primaryPrice.Amount} ${primaryPrice.Currency}/${period}`,
  });

  const addonSum = SetAddonPrices(primaryPrice, coverageAddons);

  coverageAddons.forEach(x => {
    result.push({
      title: x?.texts?.header ?? x.Name,
      price: x?.texts?.price ?? x.MonthlyPrice,
    });
  });

  result.push({
    title: "Totalt pris:",
    price: `${primaryPrice.Amount + addonSum} ${currency}/${period}`,
  });

  return result;
}

export function GetUpdatedNoFooterPrices(currentState: FooterState, currentCoverage: Coverage, selectedAddons: string[]): FooterState {
  const newState = DeepClone(currentState);
  const priceList = [] as Price[];
  const primaryPrice = currentCoverage.PrimaryPrice;
  let coverageAddons: IAddOn[] = currentCoverage.CoverageAddons?.filter(x => selectedAddons.includes(x.InternalCode ?? "")) ?? [];

  coverageAddons = DeepClone(coverageAddons);
  const addonSum = SetAddonPrices(primaryPrice, coverageAddons);

  priceList.push({
    title: currentCoverage.LongName,
    price: `${primaryPrice.Amount} kr/md`,
  });

  coverageAddons.forEach(x => {
    //replace "fran" here because it should be hidden in the footer area
    //will be removed after dynamic prices are fixed in IMF

    priceList.push({
      title: x?.texts?.header ?? x.Name,
      price: x?.texts?.price ?? x.MonthlyPrice,
    });
  });

  priceList.push({
    title: "Pris på forsikring",
    price: `${primaryPrice.Amount + addonSum} kr/md.`,
  });

  const { price, title } = last(priceList);

  return {
    ...newState,
    price: price,
    title: title,
    prices: priceList,
  };
}

export const FOOTER_CHANGED = "FOOTER_CHANGED";
export const FOOTER_COVERAGE_CHANGED = "FOOTER_COVERAGE_CHANGED";

export type CoverageChangedType = {
  coverage: Coverage;
  selectedAddonInternalCodes: string[];
};

export type FooterAction = {
  type: typeof FOOTER_CHANGED | typeof FOOTER_COVERAGE_CHANGED;
  payload: {
    value: boolean | CoverageChangedType;
  };
};

export const createFooterIsVisibleAction = (value: boolean): FooterAction => ({
  type: FOOTER_CHANGED,
  payload: { value },
});

export const createFooterContentAction = (value: CoverageChangedType): FooterAction => ({
  type: FOOTER_COVERAGE_CHANGED,
  payload: { value },
});

export function GetUpdatedFooterPrices(currentState: FooterState, coverage: Coverage, selectedAddonInternalCodes: string[]): FooterState {
  const newState = DeepClone(currentState);
  const priceList = PreparePriceList(coverage, selectedAddonInternalCodes);

  const { price, title } = last(priceList);

  return {
    ...newState,
    price,
    title,
    prices: priceList,
    secondaryPrice: coverage?.SecondaryPrice,
  };
}

export function GetUpdatedFooterPricesV2(currentState: FooterState, coverage: Coverage, selectedAddonInternalCodes: string[]): FooterState {
  const newState = DeepClone(currentState);

  const priceList = PreparePriceListV2(coverage, selectedAddonInternalCodes);

  const { price, title } = last(priceList);

  return {
    ...newState,
    price,
    title,
    prices: priceList,
    secondaryPrice: coverage?.SecondaryPrice,
  };
}

function last<T>(list: T[]): T {
  return list[list.length - 1];
}

export const buildReducer = (api = "v1"): Reducer<FooterState> => {
  return (state: FooterState = footerDefaults, input: Action): FooterState => {
    const action = input as FooterAction;
    switch (action?.type) {
      case FOOTER_CHANGED:
        return { ...state, expanded: action.payload.value as boolean };
      case FOOTER_COVERAGE_CHANGED:
        const data = action.payload.value as CoverageChangedType;
        switch (api) {
          case "v2":
            return GetUpdatedFooterPricesV2(state, data.coverage, data.selectedAddonInternalCodes);
          default:
            return GetUpdatedFooterPrices(state, data.coverage, data.selectedAddonInternalCodes);
        }
      default:
        return state;
    }
  };
};

export const actions = {
  footerExpanded:
    (value: boolean): AppThunkAction<FooterAction> =>
    dispatch =>
      dispatch(createFooterIsVisibleAction(value)),

  coverageChanged:
    (coverage: Coverage, selectedAddonInternalCodes: string[]): AppThunkAction<FooterAction> =>
    dispatch =>
      dispatch(createFooterContentAction({ coverage, selectedAddonInternalCodes: selectedAddonInternalCodes })),
};
